import React from 'react';

type ImgProps = React.ImgHTMLAttributes<HTMLImageElement>;

interface SmartImageProps extends ImgProps {
    /** Fallback: la imagen original importada (PNG/JPG). */
    src: string;
    alt: string;
    /** Variante AVIF importada. Ver nota sobre el hash mas abajo. */
    avif?: string;
    /** Variante WebP importada. */
    webp?: string;
    /** true para imagenes visibles al entrar (hero, logo): evita lazy. */
    priority?: boolean;
}

/**
 * Sirve AVIF con fallback a WebP y, por ultimo, al formato original.
 *
 * IMPORTANTE: las variantes se pasan como props importadas, no derivando la
 * URL del `src`. CRA nombra los assets con un hash del CONTENIDO
 * (`imagen.<hash>.png`), asi que el .avif de la misma imagen tiene un hash
 * distinto: cambiar solo la extension sobre la URL final daria 404.
 *
 * Uso:
 *   import hero from '../assets/hero.png';
 *   import heroAvif from '../assets/hero.avif';
 *   import heroWebp from '../assets/hero.webp';
 *   <SmartImage src={hero} avif={heroAvif} webp={heroWebp} alt="..." />
 */
const SmartImage: React.FC<SmartImageProps> = ({
    src,
    alt,
    avif,
    webp,
    priority = false,
    ...rest
}) => {
    const loading = rest.loading ?? (priority ? 'eager' : 'lazy');
    const decoding = rest.decoding ?? (priority ? 'sync' : 'async');
    const img = <img src={src} alt={alt} {...rest} loading={loading} decoding={decoding} />;

    // Sin variantes no hay nada que negociar: se sirve el original.
    if (!avif && !webp) return img;

    return (
        <picture>
            {avif && <source srcSet={avif} type="image/avif" />}
            {webp && <source srcSet={webp} type="image/webp" />}
            {img}
        </picture>
    );
};

export default SmartImage;
